import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/dates";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  Upload, FileImage, FileText, FileSpreadsheet, Sparkles, Check, X,
  Building, Calendar, Euro, Receipt, AlertTriangle, RefreshCcw,
  Search, Trash2, History,
} from "lucide-react";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.xlsx,.xls,.csv";

const CATEGORIES = [
  "utenze", "manutenzione", "alimentari", "lavanderia", "pulizia",
  "biancheria", "tasse", "iva", "tassa_soggiorno", "prodotti_colazione",
  "marketing", "commissioni", "hardware", "software", "telefonia",
  "internet", "assicurazioni", "consulenza", "formazione", "ammortamenti",
  "stipendi", "contributi", "altro",
];

const MACRO_CATEGORIES = [
  { value: "gestione_struttura", label: "Gestione struttura" },
  { value: "marketing", label: "Marketing & vendite" },
  { value: "amministrazione", label: "Amministrazione" },
  { value: "tasse_e_oneri", label: "Tasse & oneri" },
  { value: "altro", label: "Altro" },
];

export default function FinanceImportTab() {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hSearch, setHSearch] = useState("");
  const [hType, setHType] = useState("all");
  const [hFrom, setHFrom] = useState("");
  const [hTo, setHTo] = useState("");

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (hSearch.trim()) params.set("q", hSearch.trim());
      if (hType !== "all") params.set("type", hType);
      if (hFrom) params.set("from", hFrom);
      if (hTo) params.set("to", hTo);
      const r = await api.get(`/admin/finance/ocr-imports?${params.toString()}`);
      setHistory(r.data.imports || []);
    } catch (e) {
      // ignore — first-time may have no records
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deleteImport = async (id) => {
    if (!window.confirm("Eliminare questo import dal bilancio?")) return;
    try {
      await api.delete(`/admin/finance/ocr-imports/${id}`);
      toast.success("Eliminato dal bilancio");
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"));
    }
  };

  // URL / Drive import
  const [importMode, setImportMode] = useState("device"); // device | url | drive
  const [importUrl, setImportUrl] = useState("");

  const parseFromUrl = async () => {
    const u = importUrl.trim();
    if (!u) { toast.error("Inserisci un URL valido"); return; }
    setParsing(true);
    setParsed(null);
    try {
      const r = await api.post("/admin/finance/import/parse-url", { url: u });
      setParsed(r.data);
      toast.success("Documento analizzato dall'AI");
    } catch (e) {
      toast.error(errMsg(e, "Errore download/parsing"));
    } finally {
      setParsing(false);
    }
  };

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("File troppo grande (max 20 MB)");
      return;
    }
    setFile(f);
    setParsed(null);
  };

  const parse = async () => {
    if (!file) return;
    setParsing(true);
    setParsed(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/finance/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setParsed(res.data);
      toast.success("Documento analizzato!");
    } catch (e) {
      toast.error(errMsg(e, "Errore analisi documento"));
    } finally {
      setParsing(false);
    }
  };

  const confirm = async () => {
    if (!parsed) return;
    setConfirming(true);
    try {
      await api.post("/admin/finance/import/confirm", {
        type: parsed.type || "expense",
        date: parsed.date,
        total_amount: parsed.total_amount,
        vat_amount: parsed.vat_amount,
        supplier_name: parsed.supplier_name,
        category: parsed.category,
        macro_category: parsed.macro_category,
        description: parsed.description,
        vat_number: parsed.vat_number,
        invoice_number: parsed.invoice_number,
      });
      toast.success("Salvato in bilancio");
      setFile(null);
      setParsed(null);
      if (inputRef.current) inputRef.current.value = "";
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setFile(null);
    setParsed(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const fileIcon = () => {
    if (!file) return Upload;
    const n = file.name.toLowerCase();
    if (n.endsWith(".pdf")) return FileText;
    if (n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv")) return FileSpreadsheet;
    return FileImage;
  };
  const FileIc = fileIcon();

  const confidenceBadge = () => {
    const c = parsed?.confidence ?? 0;
    if (c >= 0.8) return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300" variant="outline">Alta confidenza ({Math.round(c * 100)}%)</Badge>;
    if (c >= 0.5) return <Badge className="bg-amber-100 text-amber-800 border-amber-300" variant="outline">Media confidenza ({Math.round(c * 100)}%)</Badge>;
    return <Badge className="bg-rose-100 text-rose-800 border-rose-300" variant="outline"><AlertTriangle className="w-3 h-3 mr-1" />Bassa confidenza ({Math.round(c * 100)}%) · controlla</Badge>;
  };

  return (
    <div className="space-y-5" data-testid="finance-import-tab">
      <div>
        <h2 className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-[#AC5D45]" />Import intelligente fatture & ricevute
        </h2>
        <p className="text-sm text-[#635F59] mt-1 max-w-2xl">
          Carica un'immagine, un PDF o un foglio Excel di una fattura/ricevuta. L'AI legge il documento ed estrae automaticamente data, importo, IVA, fornitore e categoria. Tu controlli e confermi.
        </p>
      </div>

      {/* Drop zone + source switcher */}
      {!parsed && (
        <>
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-1 inline-flex gap-1" data-testid="fi-source-tabs">
          {[
            { id: "device", label: "Dal dispositivo", icon: Upload },
            { id: "url", label: "Da URL", icon: FileText },
            { id: "drive", label: "Google Drive", icon: FileImage },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setImportMode(m.id)}
              data-testid={`fi-source-${m.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${importMode === m.id ? "bg-[#AC5D45] text-white" : "text-[#635F59] hover:bg-[#FDFBF7]"}`}
            >
              <m.icon className="w-3.5 h-3.5" />
              {m.label}
            </button>
          ))}
        </div>

        {importMode === "device" && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onPick(e.dataTransfer.files?.[0]);
          }}
          onClick={() => inputRef.current?.click()}
          data-testid="finance-import-drop"
          className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition ${
            dragOver
              ? "border-[#AC5D45] bg-[#FDF1E6]"
              : file
              ? "border-emerald-300 bg-emerald-50/50"
              : "border-[#E8E2D9] bg-[#FDFBF7] hover:border-[#AC5D45] hover:bg-[#FDF1E6]/40"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => onPick(e.target.files?.[0])}
            className="hidden"
            data-testid="finance-import-input"
          />
          <FileIc className={`w-12 h-12 mx-auto mb-3 ${file ? "text-emerald-600" : "text-[#AC5D45]"}`} />
          {file ? (
            <>
              <p className="font-semibold text-[#1E3A5F]">{file.name}</p>
              <p className="text-xs text-[#635F59] mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type || "—"}</p>
              <div className="flex gap-2 justify-center mt-4">
                <Button variant="outline" onClick={(e) => { e.stopPropagation(); reset(); }} data-testid="finance-import-reset">
                  <X className="w-4 h-4 mr-1" />Cambia file
                </Button>
                <Button
                  disabled={parsing}
                  onClick={(e) => { e.stopPropagation(); parse(); }}
                  data-testid="finance-import-parse"
                  className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
                >
                  <Sparkles className={`w-4 h-4 mr-1 ${parsing ? "animate-spin" : ""}`} />
                  {parsing ? "Analisi AI in corso..." : "Analizza con AI"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="font-semibold text-[#1E3A5F]">Trascina qui il documento o clicca per selezionare</p>
              <p className="text-xs text-[#635F59] mt-1">Immagine (JPG/PNG/WEBP), PDF, Excel o CSV · max 20 MB</p>
              <div className="flex justify-center gap-4 mt-4 text-[#635F59] text-xs">
                <span className="inline-flex items-center gap-1"><FileImage className="w-4 h-4" />Foto</span>
                <span className="inline-flex items-center gap-1"><FileText className="w-4 h-4" />PDF</span>
                <span className="inline-flex items-center gap-1"><FileSpreadsheet className="w-4 h-4" />Excel</span>
              </div>
            </>
          )}
        </div>
        )}

        {(importMode === "url" || importMode === "drive") && (
          <div className="bg-white border border-[#E8E2D9] rounded-2xl p-6 space-y-4" data-testid={`fi-${importMode}-panel`}>
            <div className="flex items-center gap-2">
              {importMode === "drive"
                ? <FileImage className="w-5 h-5 text-[#AC5D45]" />
                : <FileText className="w-5 h-5 text-[#AC5D45]" />}
              <h3 className="font-semibold text-[#1E3A5F]">
                {importMode === "drive" ? "Importa da Google Drive" : "Importa da URL"}
              </h3>
            </div>
            {importMode === "drive" ? (
              <div className="text-xs text-[#635F59] bg-amber-50 border border-amber-200 rounded-lg p-3 leading-relaxed">
                <strong>Come fare:</strong> apri il file su Google Drive → click destro → <em>Condividi</em> → imposta <em>"Chiunque abbia il link"</em> → copia il link qui sotto.
                <br />Funziona con qualsiasi link condiviso pubblico di Drive, Dropbox o cloud.
              </div>
            ) : (
              <p className="text-xs text-[#635F59]">
                Incolla un link diretto a un'immagine, PDF o foglio Excel di una fattura/ricevuta. L'AI scaricherà ed elaborerà il documento.
              </p>
            )}
            <div>
              <Label className="text-xs">URL del documento *</Label>
              <Input
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder={importMode === "drive" ? "https://drive.google.com/file/d/..." : "https://esempio.com/fattura.pdf"}
                data-testid="fi-url-input"
                className="mt-1.5 h-10 font-mono text-xs"
                onKeyDown={(e) => e.key === "Enter" && parseFromUrl()}
              />
            </div>
            <Button
              onClick={parseFromUrl}
              disabled={parsing || !importUrl.trim()}
              data-testid="fi-url-parse"
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            >
              <Sparkles className={`w-4 h-4 mr-1 ${parsing ? "animate-spin" : ""}`} />
              {parsing ? "Scarico e analizzo..." : "Scarica e analizza con AI"}
            </Button>
          </div>
        )}
        </>
      )}

      {/* Parsed result form */}
      {parsed && (
        <div className="bg-white border border-[#E8E2D9] rounded-2xl p-5 space-y-4" data-testid="finance-import-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-[#1E3A5F]">Risultato analisi AI</h3>
            </div>
            <div className="flex items-center gap-2">
              {confidenceBadge()}
              <Button size="sm" variant="ghost" onClick={() => { setParsed(null); }} data-testid="finance-import-redo">
                <RefreshCcw className="w-3.5 h-3.5 mr-1" />Rianalizza
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><Building className="w-3 h-3" />Fornitore</Label>
              <Input
                value={parsed.supplier_name || ""}
                onChange={(e) => setParsed({ ...parsed, supplier_name: e.target.value })}
                data-testid="fi-supplier"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" />Data</Label>
              <DateInputIT value={parsed.date || ""}
                onChange={(e) => setParsed({ ...parsed, date: e.target.value })}
                data-testid="fi-date"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Euro className="w-3 h-3" />Totale (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={parsed.total_amount || 0}
                onChange={(e) => setParsed({ ...parsed, total_amount: parseFloat(e.target.value) || 0 })}
                data-testid="fi-amount"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Euro className="w-3 h-3" />di cui IVA (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={parsed.vat_amount || 0}
                onChange={(e) => setParsed({ ...parsed, vat_amount: parseFloat(e.target.value) || 0 })}
                data-testid="fi-vat"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={parsed.type || "expense"} onValueChange={(v) => setParsed({ ...parsed, type: v })}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="fi-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">Spesa</SelectItem>
                  <SelectItem value="income">Ricavo</SelectItem>
                  <SelectItem value="tax">Tassa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select value={parsed.category || "altro"} onValueChange={(v) => setParsed({ ...parsed, category: v })}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="fi-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Macro-categoria</Label>
              <Select value={parsed.macro_category || "altro"} onValueChange={(v) => setParsed({ ...parsed, macro_category: v })}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="fi-macro">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MACRO_CATEGORIES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">N. fattura</Label>
              <Input
                value={parsed.invoice_number || ""}
                onChange={(e) => setParsed({ ...parsed, invoice_number: e.target.value })}
                placeholder="—"
                data-testid="fi-invoice-num"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs">P. IVA fornitore</Label>
              <Input
                value={parsed.vat_number || ""}
                onChange={(e) => setParsed({ ...parsed, vat_number: e.target.value })}
                placeholder="—"
                data-testid="fi-vat-num"
                className="mt-1.5 h-10"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Descrizione</Label>
              <Textarea
                rows={2}
                value={parsed.description || ""}
                onChange={(e) => setParsed({ ...parsed, description: e.target.value })}
                data-testid="fi-description"
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-3 border-t border-[#E8E2D9]">
            <Button variant="outline" onClick={reset}>Annulla</Button>
            <Button
              onClick={confirm}
              disabled={confirming || !parsed.total_amount}
              data-testid="fi-confirm"
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            >
              <Receipt className="w-4 h-4 mr-1" />
              {confirming ? "Salvataggio..." : "Salva in bilancio"}
            </Button>
          </div>
        </div>
      )}

      {/* ====== History / Filter table ====== */}
      <div className="bg-white border border-[#E8E2D9] rounded-2xl p-5 space-y-3" data-testid="finance-import-history">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-semibold text-[#1E3A5F] flex items-center gap-2">
            <History className="w-5 h-5 text-[#AC5D45]" />Storico import AI
            <Badge variant="outline" className="ml-1 text-[10px]">{history.length}</Badge>
          </h3>
          <Button size="sm" variant="ghost" onClick={loadHistory} data-testid="fi-history-refresh">
            <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${historyLoading ? "animate-spin" : ""}`} />Aggiorna
          </Button>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-5 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#635F59]" />
            <Input
              placeholder="Cerca fornitore, fattura, categoria..."
              value={hSearch}
              onChange={(e) => setHSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadHistory()}
              data-testid="fi-history-search"
              className="pl-9 h-10"
            />
          </div>
          <div className="md:col-span-2">
            <Select value={hType} onValueChange={(v) => { setHType(v); }}>
              <SelectTrigger className="h-10" data-testid="fi-history-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="expense">Spesa</SelectItem>
                <SelectItem value="income">Ricavo</SelectItem>
                <SelectItem value="tax">Tassa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <DateInputIT value={hFrom} onChange={(e) => setHFrom(e.target.value)} data-testid="fi-history-from" className="h-10" placeholder="Da" />
          </div>
          <div className="md:col-span-2">
            <DateInputIT value={hTo} onChange={(e) => setHTo(e.target.value)} data-testid="fi-history-to" className="h-10" placeholder="A" />
          </div>
          <div className="md:col-span-1 flex gap-1">
            <Button onClick={loadHistory} className="h-10 w-full bg-[#1E3A5F] hover:bg-[#15294A] text-white" data-testid="fi-history-filter">Filtra</Button>
          </div>
          {(hSearch || hType !== "all" || hFrom || hTo) && (
            <div className="md:col-span-12 text-right -mt-1">
              <button
                type="button"
                onClick={() => { setHSearch(""); setHType("all"); setHFrom(""); setHTo(""); setTimeout(loadHistory, 0); }}
                className="text-[11px] text-[#AC5D45] hover:underline"
                data-testid="fi-history-clear"
              >
                Pulisci filtri
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table data-testid="fi-history-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Data</TableHead>
                <TableHead className="w-[90px]">Tipo</TableHead>
                <TableHead>Fornitore</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>N. fattura</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-6 text-[#635F59]">Caricamento...</TableCell></TableRow>
              ) : history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6 text-[#635F59]" data-testid="fi-history-empty">
                    Nessuna fattura importata. Carica un documento qui sopra per iniziare.
                  </TableCell>
                </TableRow>
              ) : (
                history.map((it) => (
                  <TableRow key={it.id} data-testid={`fi-row-${it.id}`}>
                    <TableCell className="text-sm">{it.date ? formatDate(it.date) : "—"}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          it.type === "income"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                            : it.type === "tax"
                            ? "bg-amber-50 text-amber-700 border-amber-300"
                            : "bg-rose-50 text-rose-700 border-rose-300"
                        }
                      >
                        {it.type === "income" ? "Ricavo" : it.type === "tax" ? "Tassa" : "Spesa"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      <div className="text-[#1E3A5F]">{it.supplier_name || "—"}</div>
                      {it.vat_number && <div className="text-[10px] text-[#635F59]">P.IVA {it.vat_number}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-[#635F59] capitalize">{(it.category || "—").replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-xs font-mono text-[#635F59]">{it.invoice_number || "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-[#1E3A5F]">€{Number(it.amount || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-xs text-[#635F59]">€{Number(it.vat_amount || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-rose-600 hover:bg-rose-50 h-8 w-8"
                        onClick={() => deleteImport(it.id)}
                        data-testid={`fi-delete-${it.id}`}
                        title="Elimina"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
